import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useGetCategories, useGetZones, usePage } from '@wbk/contentful/api';
import { useParams } from 'react-router-dom';
import FullpageAnimatedLogo from '@/components/ui/Loading/FullpageAnimatedLogo';
import Seo from '@/components/headers/seo';
import Error404 from '@/components/error/404';
import ExploreSections from '@/components/web-sections/ExploreSections';
import ExploreProvider from '@/context/explore/provider';

const Explore = () => {
  const { lang } = useParams<{ lang: Language }>();
  const [hideVideo, setHideVideo] = useState(false);
  const footer = document.getElementById('footer');

  const {
    data: explore,
    isLoading,
    error,
  } = usePage({ lang, limit: 1, where: { slug: 'riyadh-season-2025-explore' } });

  const { data: categories, isLoading: isLoadingCategories } = useGetCategories({
    lang,
    where: { childrenCollection_exists: true },
  });

  const { data: zones, isLoading: isGettingZones } = useGetZones({
    lang,
    where: {
      OR: [],
      season: { slug: 'riyadh-season-2025' },
      visibility_not: null,
    },
  });

  useEffect(() => {
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setHideVideo(entry.isIntersecting);
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(footer);

    return () => {
      observer.disconnect();
    };
  }, [footer]);

  if (isLoading || isLoadingCategories || isGettingZones) {
    return <FullpageAnimatedLogo />;
  }

  if (!explore || error) {
    return <Error404 />;
  }

  return (
    <>
      <Seo
        title={explore.seo?.title || explore.title}
        description={explore.seo?.description}
        image={explore.seo?.image?.url}
      />

      <ExploreProvider categories={categories} zones={zones?.items}>
        <div className='relative h-full w-full pb-40'>
          <video
            className={twMerge(
              'no-controls background-video pointer-events-none fixed left-1/2 top-0 h-full w-full -translate-x-1/2 object-cover opacity-30',
              hideVideo && 'opacity-0'
            )}
            src={explore.background?.url}
            autoPlay
            loop
            muted
            playsInline
            style={{ zIndex: -1 }}
          />

          <div className='space-y-24'>
            {explore?.sectionsCollection.items.map((section, idx) => (
              <ExploreSections
                key={section.__typename + idx}
                section={section.__typename === 'WebSection' ? section : undefined}
              />
            ))}
          </div>
        </div>
      </ExploreProvider>
    </>
  );
};

export default Explore;
